import React, { useEffect, useState, useMemo } from "react";
import { debounce } from "throttle-debounce";
import { useHistory, useParams } from "react-router-dom";
import { connect } from "react-redux";
import PanelHeading from "react-bootstrap/lib/PanelHeading";
import PanelTitle from "react-bootstrap/lib/PanelTitle";
import PanelBody from "react-bootstrap/lib/PanelBody";
import * as Api from '../../../../../api/cp/widgetPlayer';
import Breadcrumbs from "../../../UI/Breadcrumbs";
import toast, { Toaster } from "react-hot-toast";
import { Checkbox, ControlLabel, Button, FormControl, FormGroup, Panel, PanelGroup } from "react-bootstrap";
import SettingTabWrapper from "../components/SettingTabWrapper";
import { themeColorTitles, themeColorsOptions } from "../../EventSettings/models";
import { THEME_COLOR_BACKGROUND, THEME_COLOR_TEXT, THEME_DARK } from "../../EventSettings/constants";
import Preview from "./Preview";
import locale from "../../../../locale";
import WidgetLanguageSetting, { LANGUAGE } from "../components/Settings/WidgetLanguageSetting";
import * as commonConstants from '../constants';
import CPSettingSize from "../../../UI/Settings/CPSettingSize";
import CPSettingRadio from "../../../UI/Settings/CPSettingRadio";
import { PagesProvider, usePagesContext } from "../../PagesSettings/context/PagesContext";
import PlayerEmbedCode from "./PlayerEmbedCode";
import { getEventModuleAuthExtarnal } from "../../../../../api/cp/eventModuleAuth";
import { SIDEBAR_POSITION, SIDEBAR_POSITION_OPTIONS } from "./constants";

const WidgetPlayerEditor = ({ event }) => {
    const history = useHistory();
    const { widgetKey } = useParams();
    const [settings, setSettings] = useState({});
    const [isSaving, setSaving] = useState(false);
    const [params, setParams] = useState(null);
    const [authExtarnalData, setAuthExtarnalData] = useState({});
    const breadcrumbs = [
        'mainMenu.widgets',
        'mainMenu.widget.platform-player',
        'mainMenu.widget.update',
    ];

    useEffect(() => {
        fetchWidgetSettings();
    }, [widgetKey]);

    useEffect(() => {
        setParams({
            ...params,
            widgetKey: widgetKey,
            lang: settings?.lang || null,
        });
    }, [settings, widgetKey]);

    useEffect(() => {
        if (event.moduleAuth.email) {
            fetchExternalAuthData();
        }
    }, [event]);

    function handleChangeSettings(key, value) {
        setSettings(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    async function fetchWidgetSettings() {
        try {
            const response = await Api.getWidgetPlayerSettings(widgetKey);
            setSettings(response || {});
        } catch (error) {
            console.error(error);
        }
    }

    async function fetchExternalAuthData() {
        try {
            const response = await getEventModuleAuthExtarnal(event.eventKey);
            setAuthExtarnalData(response || {});
        } catch (error) {
            console.error(error);
        }
    }

    async function handleSaveModel() {
        try {
            setSaving(true);
            await handleEditWidget(settings);
            setSaving(false);
            toast.success('Настройки виджета успешно сохранены!', {
                duration: 4000,
            });
        } catch (error) {
            console.error(error);
        }
    }

    async function handleEditWidget(model) {
        try {
            const response = await Api.updateWidgetPlayer(widgetKey, model);
            setSettings(response);
        } catch (error) {
            console.error(error);
        }
    }

    const handleThemeChange = (value) => {
        const backgroundColor = value === THEME_DARK ? '16161B' : 'FFFFFF';
        const textColor = value === THEME_DARK ? 'FFFFFF' : '16161B';

        handleChangeSettings('colorTheme', value);
        handleChangeSettings(THEME_COLOR_BACKGROUND, backgroundColor);
        handleChangeSettings(THEME_COLOR_TEXT, textColor);
    };

    const handleStreamsChange = (checked, streamID, target) => {
        let targetArr = [...(settings[target] || [])];

        if (checked) {
            targetArr.push(String(streamID))
        } else {
            if (targetArr.length === 1 && target === 'streamsIds') return;
            if (!targetArr.length) {
                targetArr = event.stream?.map(({ id }) => String(id));
            }
            targetArr = targetArr.filter(id => String(id) !== String(streamID));
        }

        handleChangeSettings(target, targetArr);
    }

    const handleColorChange = debounce(500, (key, value) => {
        const color = value.replace('#', '');
        handleChangeSettings(key, color);
    });

    return (
        <section>
            <Breadcrumbs
                link={`/event/${event.eventPassword}`}
                activeCrumbs={breadcrumbs}
            />

            <div
                style={{
                    marginBottom: '60px',
                    display: 'flex',
                    columnGap: '40px'
                }}
            >
                <div style={{ width: '28%' }}>
                    <FormGroup>
                        <FormGroup style={{ display: 'flex', columnGap: '12px' }}>
                            <Button
                                bsStyle="primary"
                                disabled={isSaving}
                                onClick={handleSaveModel}
                            >
                                Сохранить
                            </Button>
                            <Button
                                disabled={isSaving}
                                onClick={() => history.push(`/event/${event.eventPassword}/widgets/broadcast`)}
                            >
                                Закрыть
                            </Button>
                        </FormGroup>

                        <FormGroup>
                            <FormControl
                                type="text"
                                placeholder="Введите название виджета"
                                maxLength={50}
                                value={settings?.name || ''}
                                onChange={(e) => handleChangeSettings('name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <SettingTabWrapper
                                title="Общие настройки"
                                label="commonSettings"
                            >
                                <WidgetLanguageSetting
                                    lang={settings[LANGUAGE]}
                                    onUpdate={handleChangeSettings}
                                />
                                <FormGroup style={{ marginBottom: "2rem" }}>
                                    <ControlLabel>
                                        Цветовая схема
                                    </ControlLabel>
                                    <FormGroup>
                                        <FormControl
                                            componentClass="select"
                                            name="eventTheme"
                                            value={settings.colorTheme}
                                            onChange={(e) => handleThemeChange(e.target.value)}
                                        >
                                            {Object.entries(themeColorTitles).map(([theme, name]) => (
                                                <option
                                                    key={theme}
                                                    value={theme}
                                                >
                                                    {name}
                                                </option>
                                            ))}
                                        </FormControl>
                                    </FormGroup>
                                    {themeColorsOptions.map((color) => (
                                        <FormGroup
                                            key={color.key}
                                            style={{ display: 'flex', alignItems: 'center' }}
                                        >
                                            <input
                                                type="color"
                                                value={`#${settings[color.key]}`}
                                                onChange={(e) => handleColorChange(color.key, e.target.value)}
                                            />
                                            <span>&nbsp; {color.title}</span>
                                        </FormGroup>
                                    ))}
                                </FormGroup>
                                <FormGroup>
                                    <Checkbox
                                        checked={settings.transparentBackground}
                                        onChange={(e) => handleChangeSettings('transparentBackground', e.target.checked)}
                                    >Сделать фон под плеером прозрачным</Checkbox>
                                </FormGroup>
                                <hr />
                                <CPSettingSize
                                    name={commonConstants.BORDER_RADIUS_SIZE}
                                    label={locale('settings.borderRadiusSize')}
                                    eventValue={settings?.[commonConstants.BORDER_RADIUS_SIZE]}
                                    defaultValue={'4px'}
                                    onChange={(value) => handleChangeSettings(commonConstants.BORDER_RADIUS_SIZE, value[commonConstants.BORDER_RADIUS_SIZE])}
                                />
                                <hr />
                                <Checkbox
                                    checked={settings.showCountdown}
                                    onChange={(e) => handleChangeSettings('showCountdown', e.target.checked)}
                                >
                                    {locale('settings.showCountdown')}
                                </Checkbox>
                            </SettingTabWrapper>
                            <SettingTabWrapper
                                title="Настройка залов"
                                label="hallsSettings"
                            >
                                <FormGroup style={{ marginBottom: "2rem" }}>
                                    <Checkbox
                                        checked={settings.streamsTabsHidden}
                                        onChange={(e) => handleChangeSettings('streamsTabsHidden', e.target.checked)}
                                    >Скрывать выбор залов</Checkbox>
                                </FormGroup>
                                <FormGroup>
                                    <ControlLabel>Какие залы показывать?</ControlLabel>
                                    {event.stream?.map((stream) => {
                                        const checked = !settings.streamsIds?.length || settings.streamsIds?.includes(String(stream.id));
                                        return <Checkbox key={stream.id}
                                            checked={checked}
                                            onChange={(e) => handleStreamsChange(e.target.checked, stream.id, 'streamsIds')}
                                            disabled={settings.streamsIds?.length === 1 && checked}
                                        >{stream.description_ru || stream.description_en}</Checkbox>
                                    })}
                                </FormGroup>

                                {event.moduleComment.enabled &&
                                    <FormGroup>
                                        <ControlLabel>В каких залах выводить чат?</ControlLabel>
                                        {event.stream?.filter(({ id }) => !settings.streamsIds?.length || settings.streamsIds?.includes(String(id)))?.map((stream) => {
                                            const checked = settings.allowCommentsStreamIds?.includes(String(stream.id));
                                            return <Checkbox key={stream.id}
                                                checked={checked}
                                                onChange={(e) => handleStreamsChange(e.target.checked, stream.id, 'allowCommentsStreamIds')}
                                            >{stream.description_ru || stream.description_en}</Checkbox>
                                        })}
                                    </FormGroup>
                                }

                                {event.moduleQuestion.enabled &&
                                    <FormGroup>
                                        <ControlLabel>В каких залах выводить вопросы?</ControlLabel>
                                        {event.stream?.filter(({ id }) => !settings.streamsIds?.length || settings.streamsIds?.includes(String(id)))?.map((stream) => {
                                            const checked = settings.allowQuestionsStreamIds?.includes(String(stream.id));
                                            return <Checkbox key={stream.id}
                                                checked={checked}
                                                onChange={(e) => handleStreamsChange(e.target.checked, stream.id, 'allowQuestionsStreamIds')}
                                            >{stream.description_ru || stream.description_en}</Checkbox>
                                        })}
                                    </FormGroup>
                                }
                            </SettingTabWrapper>
                            <SettingTabWrapper
                                title="Настройка блоков"
                                label="blocksSettings"
                            >
                                {((event.moduleComment.enabled && !!settings.allowCommentsStreamIds?.length)
                                    || (event.moduleQuestion.enabled && !!settings.allowQuestionsStreamIds?.length))
                                    && (<>
                                        <CPSettingRadio
                                            name={SIDEBAR_POSITION}
                                            eventValue={settings?.[SIDEBAR_POSITION]}
                                            defaultValue="right"
                                            label={locale('settings.sidebarPosition')}
                                            options={SIDEBAR_POSITION_OPTIONS}
                                            onChange={(value) => handleChangeSettings(SIDEBAR_POSITION, value[SIDEBAR_POSITION])}
                                        />
                                        <hr />
                                    </>
                                )}
                                <PagesProvider
                                    event={event}
                                    setPagesLoaded={() => { }}
                                >
                                    <BlocksEditor settings={settings} handleChangeSettings={handleChangeSettings} />
                                </PagesProvider>
                            </SettingTabWrapper>
                        </FormGroup>
                    </FormGroup>

                </div>
                <div style={{ width: '72%' }}>
                    <PanelGroup
                        accordion
                        id="widget-code"
                    >
                        <Panel eventKey="widget-code">
                            <PanelHeading>
                                <PanelTitle toggle>
                                    Код для встраивания плеера
                                </PanelTitle>
                            </PanelHeading>
                            <PanelBody collapsible>
                                <pre style={{ whiteSpace: "initial" }}>
                                    &lt;script src="{location.origin}
                                    /widget/static/js/embed-player.js" {settings?.lang && `lang="${settings.lang}" `}data-watchKey="{event.eventKey}" data-widgetKey="{widgetKey}"&gt;&lt;/script&gt;
                                    <br />
                                </pre>
                            </PanelBody>
                        </Panel>
                    </PanelGroup>
                    {(event.moduleAuth.guest || event.moduleAuth.email) && (
                        <PlayerEmbedCode
                            eventKey={event.eventKey}
                            isRegistrationTypeEmail={event.moduleAuth.email}
                            widgetKey={widgetKey}
                            privateKey={authExtarnalData?.privateKey || ''}
                            registrationFields={event.registrationFields}
                        />
                    )}
                    <Preview
                        event={event}
                        settings={settings}
                        params={params}
                    />
                </div>
            </div>

            <Toaster position="top-right" />
        </section>
    );
};

const mapStateToProps = (store) => {
    return {
        event: store.currentEvent,
    };
};

export default connect(mapStateToProps)(WidgetPlayerEditor);

const BlocksEditor = ({ settings, handleChangeSettings }) => {
    const { pages } = usePagesContext();

    const blocks = useMemo(() => ((pages || []).find(({ type }) => type === 'broadcast')?.blocks || []).filter(({ unremovable }) => !unremovable), [pages]);
    const hiddenBlocks = useMemo(() => {
        const _hiddenBlocks = settings?.hiddenBlocks || [];
        return _hiddenBlocks
            .map(blockID => Number.isFinite(blockID) ? blockID : blocks.filter(({ template }) => template === blockID).map(({ id }) => id))
            .reduce((prev, next) => prev.concat(next), []);
    }, [settings, blocks]);

    const handleBlocksChange = (blockID, checked) => {
        let result = [...hiddenBlocks];
        if (checked) {
            result.push(blockID)
        } else {
            result = result.filter(id => id !== blockID)
        }
        handleChangeSettings('hiddenBlocks', result);
    }

    return <>
        {blocks.map(block => {
            return <FormGroup style={{ marginBottom: "2rem" }} key={block.id}>
                <Checkbox
                    checked={hiddenBlocks.includes(block.id)}
                    onChange={(e) => handleBlocksChange(block.id, e.target.checked)}
                >Скрывать блок <b>{locale(`settings.blocks.${block.template}`)}</b></Checkbox>
            </FormGroup>
        })}
        <hr/>
        <FormGroup style={{ marginBottom: "2rem" }}>
            <Checkbox
                checked={settings.scheduleModalAddCalendarButtonHidden}
                onChange={(e) => handleChangeSettings('scheduleModalAddCalendarButtonHidden', e.target.checked)}
            >Скрывать кнопку "Добавить в календарь" в окне деталей эпизода</Checkbox>
        </FormGroup>
    </>
}
